/**
 * Date Utility Functions
 *
 * 날짜 계산 관련 유틸리티 함수들
 * date-fns를 기반으로 구현하여 프로젝트 전체와 일관성 유지
 */

import { endOfMonth } from 'date-fns'

/**
 * 해당 년월의 마지막 날짜를 반환
 *
 * @param year 년도 (예: 2025)
 * @param month 월 (1-12)
 * @returns 마지막 날짜 (1-31)
 *
 * @example
 * getLastDayOfMonth(2025, 2)  // 28 (평년)
 * getLastDayOfMonth(2024, 2)  // 29 (윤년)
 * getLastDayOfMonth(2025, 4)  // 30
 * getLastDayOfMonth(2025, 12) // 31
 */
export function getLastDayOfMonth(year: number, month: number): number {
  // Date 객체는 month가 0-based이므로 -1
  const date = new Date(year, month - 1, 1)
  const lastDate = endOfMonth(date)
  return lastDate.getDate()
}

/**
 * 해당 년월의 날짜 범위를 YYYY-MM-DD 형식으로 반환
 *
 * @param year 년도
 * @param month 월 (1-12)
 * @returns [startDate, endDate] 튜플
 *
 * @example
 * getMonthDateRange(2025, 2)
 * // ['2025-02-01', '2025-02-28']
 *
 * getMonthDateRange(2024, 2)
 * // ['2024-02-01', '2024-02-29'] (윤년)
 */
export function getMonthDateRange(year: number, month: number): [string, string] {
  const startDate = `${year}-${String(month).padStart(2, '0')}-01`
  const lastDay = getLastDayOfMonth(year, month)
  const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`

  return [startDate, endDate]
}

/**
 * 현재 년월을 반환
 *
 * @returns [year, month] 튜플
 *
 * @example
 * getCurrentYearMonth()
 * // [2025, 2] (2025년 2월)
 */
export function getCurrentYearMonth(): [number, number] {
  const now = new Date()
  return [now.getFullYear(), now.getMonth() + 1]
}
